"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useProgressNavigation } from "@/components/progress-bar";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency, formatDate } from "@/lib/format-data";
import { useToast } from "@/lib/use-toast";
import type { SuperbillSummary } from "@/types/superbill";
import { createSuperbill, getSuperbillByNote, linkNoteToSuperbill, listSuperbillChargeCandidates, listSuperbills } from "@/api/provider/billing/superbill";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";

/** Which footer action is in flight — drives per-button spinners. */
type Action = "create" | "link";

/** Ties the picker's <Label> to the Select trigger. Only one dialog is ever
 *  mounted at a time (the body unmounts on close), so a constant is safe. */
const PICKER_ID = "superbill-note-picker";
export interface SuperbillNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noteId: string;
  patientId: string;
  eventId?: string | null;
  /** Override where create/link goes after it succeeds. Defaults to navigating
   *  to the full-page /billing/superbills/{id} route; the note tab passes a
   *  no-op so it flips to its inline editor in place instead of routing away. */
  onNavigateToBill?: (id: string) => void;
}

/** `#abc123 · $120.00 · Jan 3, 2026 · 2 charges` */
function summaryLabel(sb: SuperbillSummary): string {
  return [`#${sb.id.slice(-6)}`, formatCurrency(sb.totalBilledCents), formatDate(sb.createdDate), `${sb.chargeCount} charge${sb.chargeCount === 1 ? "" : "s"}`].join(" · ");
}

/**
 * Note → Super Bill dialog, shaped like its neighbour in the same action menu
 * (`link-note-to-appointment-dialog`): one labelled picker, and the two ways out
 * live in the footer rather than in an up-front mode choice — **Link** the note
 * to the picked bill, or **Create new** from the appointment's charges. When the
 * note already has a bill, a line above the picker states which one; the bill
 * itself is worked on in the note's Superbill tab, so there is no jump-to-editor
 * shortcut here (the full editor stays reachable from the patient's Billing tab
 * and the notes-list row menu).
 *
 * Charge-less bills are expected (charges are often created downstream), so
 * Create never blocks on "no charges".
 */
export default function SuperbillNoteDialog(props) {
  const $ = _c(9);
  const {
    open,
    onOpenChange
  } = props;
  let t0;
  if ($[0] !== open || $[1] !== props) {
    t0 = open && <SuperbillNoteDialogBody {...props} />;
    $[0] = open;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <DialogContent className="max-w-lg">{t0}</DialogContent>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== onOpenChange || $[6] !== open || $[7] !== t1) {
    t2 = <Dialog open={open} onOpenChange={onOpenChange}>{t1}</Dialog>;
    $[5] = onOpenChange;
    $[6] = open;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function SuperbillNoteDialogBody({
  onOpenChange,
  noteId,
  patientId,
  eventId,
  onNavigateToBill
}: SuperbillNoteDialogProps) {
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [selectedBillId, setSelectedBillId] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState<Action | undefined>(undefined);

  // The note's current link (if any) — powers the "Linked / Go to" banner.
  const byNoteQueryOptions = {
    queryKey: ["superbill-by-note", patientId, noteId],
    queryFn: () => getSuperbillByNote(patientId, noteId)
  };
  const {
    data: linkedBill,
    isError: linkedBillFailed
  } = useQuery({
    ...byNoteQueryOptions,
    enabled: !!patientId && !!noteId
  });

  // Bills to choose from in the picker (non-archived, newest first — both
  // guaranteed by the list endpoint).
  //
  // Typed as nullable even though this queryFn never resolves null: the patient
  // billing tab reads the *same* key with a queryFn that keeps the helper's null
  // on failure, so a cached null can be handed to us. See billsFailed below.
  const {
    data: bills,
    isError: billsQueryFailed,
    isLoading: billsLoading
  } = useQuery<SuperbillSummary[] | null>({
    queryKey: ["superbills", patientId],
    queryFn: async () => {
      // Throw on null so a failed request lands in the error state. This helper
      // swallows errors into null (unlike getSuperbillByNote), so without this
      // the query would *succeed* with no rows and the picker would state "No
      // Super Bills for this patient yet" — a false claim that also disables
      // Link, pushing the user to duplicate a bill that does exist. Mirrors the
      // construct-superbill wizard.
      const result = await listSuperbills(patientId);
      if (!result) throw new Error("Failed to load Superbills");
      return result;
    },
    enabled: !!patientId
  });
  // A cached null counts as failed, not as "this patient has none". The billing
  // tab caches the helper's null as a *successful* empty result under this same
  // key, and with a 5-minute staleTime we'd reuse it without refetching — so a
  // list that failed over there would make the picker here assert "No Super
  // Bills for this patient yet" and disable Link, which is exactly the false
  // claim the throw above exists to prevent.
  const billsFailed = billsQueryFailed || bills === null;
  const options = bills ?? [];

  // The Super Bill editor is a heavy route, so a bare router.push leaves the
  // user on the note with no feedback for the whole transition — it reads as a
  // dead click. navigateWithProgress drives the app's progress bar (same as the
  // notes list) and prefixes the tenant, so the hand-off stays visible after
  // the dialog closes.
  const goToBill = (id: string) => {
    if (onNavigateToBill) {
      onNavigateToBill(id);
    } else {
      navigateWithProgress(`/billing/superbills/${id}`);
    }
    onOpenChange(false);
  };

  // Invalidate the patient's whole by-note prefix, not just this revision's key.
  // getSuperbillByNote accepts any revision id and resolves to the chain root, so
  // v1 and v2 of the same note cache the link under different keys; a link change
  // made from one revision must reach the others, or switching back within the
  // stale window would show the pre-change bill (or hide the shortcut).
  const invalidateByNote = () => queryClient.invalidateQueries({
    queryKey: ["superbill-by-note", patientId]
  });

  // A freshly created bill has to show up in this patient's list. The picker
  // above and the patient billing tab share this key, and queries are stale
  // only after 5 minutes — without this the next note's picker would omit the
  // bill just created, inviting a duplicate instead of a link to it.
  const invalidateSuperbillList = () => queryClient.invalidateQueries({
    queryKey: ["superbills", patientId]
  });

  // The bill we just linked to is where we're navigating, and its detail carries
  // the noteCodes autofill this link produces. That query is stale only after 5
  // minutes, so opening a bill, coming here to link the note, and being sent
  // back would re-render the cached pre-link copy — no codes, the one thing the
  // link was for.
  const invalidateSuperbillDetail = (superbillId: string) => queryClient.invalidateQueries({
    queryKey: ["superbill", superbillId]
  });

  // The notes list carries `superbillId` per row and drives "Go to Super Bill"
  // there, so a link change has to reach it too — not just the by-note query
  // this dialog reads. Rows are fresh for 2 minutes, long enough that closing
  // the dialog and reopening the row's menu would still offer the *previous*
  // bill (or hide the shortcut on a note we just linked). Same reason
  // useUpdateNoteEventLinkMutation invalidates lists after an appointment link.
  const invalidateNoteLists = () => queryClient.invalidateQueries({
    queryKey: noteQueryKeys.lists(),
    exact: false
  });
  const handleLink = async () => {
    if (!selectedBillId) return;
    setSubmitting("link");
    try {
      const ok = await linkNoteToSuperbill(selectedBillId, noteId);
      if (!ok) throw new Error("The server returned an error.");
      await Promise.all([invalidateByNote(), invalidateSuperbillDetail(selectedBillId), invalidateNoteLists()]);
      goToBill(selectedBillId);
    } catch (error) {
      toast({
        title: "Could not save Superbill",
        description: error instanceof Error ? error.message : String(error),
        variant: "error"
      });
    } finally {
      setSubmitting(undefined);
    }
  };
  const handleCreate = async () => {
    setSubmitting("create");
    try {
      // Seed from the appointment's charges. Charge-less is allowed (charges
      // are often created downstream), so no "no charges" guard here.
      //
      // With no appointment there is nothing to seed from, and we must NOT ask
      // for candidates: omitting the event filter makes that endpoint fall back
      // to the patient's last 30 days of charges (correct for the patient-tab
      // wizard, wrong here), which would quietly fill the bill with unrelated
      // visits. Start empty instead and let the user add lines in the editor.
      let chargeIds: string[] = [];
      if (eventId) {
        const candidates = await listSuperbillChargeCandidates(patientId, {
          eventIds: [eventId]
        });
        // null is a *failed request* (the api helper swallows errors), not "this
        // appointment has no charges". Treating them alike would silently create
        // an empty bill for a visit that does have charges, with nothing to
        // explain where they went. The construct-superbill wizard refuses to
        // advance on the same failure for the same reason.
        if (!candidates) {
          throw new Error("Couldn't load this appointment's charges. Please try again.");
        }
        chargeIds = candidates.map(c => c.id);
      }

      // Always send noteId: the create endpoint upserts the link, so it moves an
      // already-linked note onto the new bill in the same transaction. Creating
      // first and moving afterwards would leave the bill created but the note
      // still on the old one whenever that second call failed, and a retry would
      // pile up unlinked duplicates.
      const created = await createSuperbill(patientId, {
        chargeIds,
        eventIds: eventId ? [eventId] : [],
        noteId
      });
      if (!created) throw new Error("The server returned an error.");
      await Promise.all([invalidateByNote(), invalidateSuperbillList(), invalidateNoteLists()]);
      goToBill(created.id);
    } catch (error_0) {
      // A create can fail precisely because someone else linked this note first
      // — the server answers 409 for the loser of that race. The api helper
      // swallows the status into null, so we cannot tell that case apart here;
      // re-read the link instead of guessing. Awaiting it before `submitting`
      // clears means the buttons stay disabled until the banner reflects the
      // winner's bill, so the retry that would create a *second* bill and move
      // the note onto it can't be fired against stale "unlinked" state.
      await invalidateByNote();
      toast({
        title: "Could not save Superbill",
        description: error_0 instanceof Error ? error_0.message : String(error_0),
        variant: "error"
      });
    } finally {
      setSubmitting(undefined);
    }
  };
  return <>
      <DialogHeader>
        <DialogTitle>Superbill</DialogTitle>
      </DialogHeader>
      <DialogBody className="space-y-4">
        {linkedBill && <p className="text-body text-foreground-secondary">
            Linked to{" "}
            <span className="text-body-strong text-foreground">
              {summaryLabel(linkedBill)}
            </span>
          </p>}

        {/* A failed lookup is indistinguishable from "not linked" — both render
            no banner. Say so, rather than letting the absence read as fact:
            Create new moves the note if a link does exist, and the user would
            otherwise have no idea that was on the table. Creating stays enabled
            — it's an explicit choice, and blocking it on a failed *read* would
            strand the common case of starting a fresh bill. */}
        {linkedBillFailed && <p className="rounded-lg border border-status-warning-text/20 bg-status-warning-bg p-3 text-body text-status-warning-text">
            Couldn&apos;t check whether this note already has a Superbill. If it
            does, creating a new one moves the note to it.
          </p>}

        {/* Only promise seeded charges when there is an appointment to seed
            from — without one, Create new starts an empty bill. */}
        <p className="text-body text-foreground-secondary">
          {eventId ? "Attach this note to a Superbill the patient already has, or create a new one seeded from this appointment's charges." : "Attach this note to a Superbill the patient already has. This note has no linked appointment, so a new bill starts empty — you can add lines in the editor."}
        </p>

        <div className="space-y-1">
          <Label htmlFor={PICKER_ID}>Superbill</Label>
          <Select value={selectedBillId} onValueChange={setSelectedBillId}
        // Nothing to choose from — don't offer an empty listbox; the
        // placeholder already explains why, and Create new still works.
        disabled={options.length === 0}>
            {/* The trigger's content is the *value*, so without an explicit
                name a screen reader announces the selected bill and never
                "Super Bill". aria-label supplies the name; htmlFor above keeps
                clicking the label focusing the trigger. */}
            <SelectTrigger id={PICKER_ID} aria-label="Superbill">
              <SelectValue placeholder={billsFailed ? "Couldn't load this patient's Superbills" :
            // An in-flight list is not an answer. Falling through to
            // the empty copy would assert the patient has no Super
            // Bills before we know, and that reads as a fact right
            // next to an enabled Create new — the duplicate this
            // dialog exists to prevent.
            billsLoading ? "Loading Superbills…" : options.length === 0 ? "No Superbills for this patient yet" : "Choose a Superbill…"} />
            </SelectTrigger>
            <SelectContent>
              {options.map(sb => <SelectItem key={sb.id} value={sb.id}
            // The bill this note is already on can't be picked: linking to
            // it is a no-op that reads like an action. Gated on the by-note
            // lookup, never on what the user has picked — an unlinked note
            // (and a note whose lookup failed, where linkedBill is
            // undefined) disables nothing, so choosing a bill still works.
            // Every *other* bill stays selectable, so this note can still
            // be moved to a different one.
            disabled={sb.id === linkedBill?.id} description={sb.id === linkedBill?.id ? "Already linked to this note" : undefined}>
                  {summaryLabel(sb)}
                </SelectItem>)}
            </SelectContent>
          </Select>
          {billsFailed && <p className="text-caption text-status-warning-text">
              This patient may already have Superbills we couldn&apos;t load.
              Close and reopen to retry before creating a new one.
            </p>}
        </div>
      </DialogBody>
      <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
        <Button type="button" variant="secondary" onClick={handleCreate} disabled={!!submitting} isLoading={submitting === "create"} loadingText="Creating…">
          Create new
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={!!submitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleLink} disabled={!!submitting || !selectedBillId} isLoading={submitting === "link"} loadingText="Linking…">
            Link
          </Button>
        </div>
      </DialogFooter>
    </>;
}